import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useEffect, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth, can, ROLE, type Permission } from '../lib/auth';
import { api } from '../lib/api';
import { currentPushStatus, enablePushNotifications, isPushSupported } from '../lib/push';

/**
 * `perm` is the capability the link's PAGE needs, not a role. That is what lets
 * "who sees the dispatch queue" change by editing one line of the API's role
 * map instead of hunting through the sidebar.
 */
interface NavItem { to: string; label: string; ready?: boolean; perm?: Permission | Permission[]; superAdminOnly?: boolean }

const SECTIONS: { heading: string; items: NavItem[] }[] = [
  {
    heading: 'Main',
    items: [
      { to: '/dashboard', label: 'Dashboard', ready: true },
      // Beside the Dashboard: every production entry waiting for approval.
      // VIEW_QC_QUEUE is held by the Quality role and Super Admin only.
      { to: '/quality', label: 'Quality', ready: true, perm: 'VIEW_QC_QUEUE' },
      // Change / Repeat orders waiting for Quality's master file (one working day).
      // Quality marks them; VPPL, GM and CRM see where they stand.
      { to: '/quality/master-files', label: 'Master File Orders', ready: true, perm: 'VIEW_MASTER_FILES' },
    ],
  },
  {
    // Each item only appears for the role that owns that step (Admin sees all).
    heading: 'My Work',
    items: [
      // Gated on VIEWING the queue, not on the action taken there — that is
      // what lets the Process Coordinator watch these screens without being
      // able to commit a delivery date or move board status.
      // Every PO in the flow, its job card, DOD and where it is on the floor.
      { to: '/orders', label: 'Orders', ready: true, perm: 'VIEW_OVERSIGHT' },
      { to: '/queue/crm', label: 'Set Delivery Date', ready: true, perm: 'VIEW_CRM_QUEUE' },
      // Design Queue was previously hidden, now exposed as the next phase for designers
      { to: '/queue/design', label: 'Design Queue', ready: true, perm: 'MARK_DESIGN_DONE' },
      // The design team's "Artwork No. Detail" sheet, in the system.
      { to: '/artworks', label: 'Artwork Register', ready: true, perm: 'MARK_DESIGN_DONE' },
      // Pharmacode and GS1 DataMatrix for carton artwork (the SimplyPharma2 tool).
      { to: '/barcodes', label: 'Barcodes', ready: true, perm: 'MANAGE_BARCODES' },
      { to: '/queue/purchase', label: 'Purchase — Board', ready: true, perm: 'VIEW_PURCHASE_QUEUE' },
      { to: '/queue/cutting', label: 'Cutting Queue', ready: true, perm: 'VIEW_CUTTING_QUEUE' },
      // The day's plan, machine by machine - next to the floor screens it feeds.
      { to: '/planner', label: 'Printing Planning', ready: true, perm: 'VIEW_PLANNER' },
      // The plan machine by machine, and what was printed against it.
      { to: '/queue/printing', label: 'Printing', ready: true, perm: 'VIEW_PRINTING_QUEUE' },
      { to: '/queue/dispatch', label: 'Dispatch Queue', ready: true, perm: 'VIEW_DISPATCH_QUEUE' },
      // Sits in My Work, not buried under Transactions with the master data —
      // it is the screen the Process Coordinator opens first each morning.
      { to: '/dod-report', label: 'Performance', ready: true, perm: 'VIEW_OVERSIGHT' },

      { to: '/edit-requests', label: 'Job Edit Requests', ready: true, perm: 'APPROVE_JOB_EDIT' },
      { to: '/print-log',     label: 'Print Log',         ready: true, perm: 'MANAGE_USERS'    },
    ],
  },
  {
    heading: 'Master Database',
    items: [
      { to: '/clients', label: 'Client Master', ready: true },
      { to: '/vendors', label: 'Vendor Master', ready: true },
      { to: '/contractors', label: 'Contractor Master', ready: true, perm: 'EDIT_CONTRACTORS' },
      { to: '/die-designs', label: 'Die Design', ready: true },
      { to: '/products', label: 'Product Master', ready: true },
      { to: '/machines', label: 'Machine', ready: true },
      // '/sheets' hidden from the sidebar — the master proved unused for
      // ongoing work, but existing job cards still reference tblSheetMaster
      // via j.Sheet_Size so nothing was deleted from the DB.  Restore
      // this line if the sheet catalogue is needed again.
      { to: '/paper-types', label: 'Paper Type', ready: true },
      { to: '/top-finishes', label: 'Lamination Type', ready: true },
      { to: '/employees', label: 'Employee', ready: true, superAdminOnly: true },
      { to: '/departments', label: 'Department', ready: true },
      { to: '/designations', label: 'Designation', ready: true, superAdminOnly: true },
      { to: '/pantone-colors', label: 'Pantone Colors', ready: true },
      { to: '/holidays', label: 'Holiday', ready: true },
      { to: '/news', label: 'News & Updates', ready: true },
    ],
  },
  {
    heading: 'Transactions',
    items: [
      // Visible to everyone: the records are readable office-wide. Creating and
      // editing them is Super Admin / VPPL, enforced on the form and the API.
      { to: '/purchase-orders', label: 'Purchase Orders', ready: true },
      { to: '/job-orders', label: 'Job Orders', ready: true },
      { to: '/slips', label: 'Packaging Slips', ready: true },
      { to: '/production', label: 'Production Entry', ready: true },
      { to: '/reports', label: 'Reports', ready: true, perm: 'VIEW_OVERSIGHT' },
      { to: '/wip-report', label: 'WIP Track Records', ready: true },
      { to: '/estimations', label: 'Estimations' },
      { to: '/progress', label: 'Progress Report' },
    ],
  },
  {
    // Three separate inventories per the user's mental model. Each scope
    // filters the same page by category (BOARD / RAW+FOIL+CONSUMABLE /
    // SPARE+TOOL) so the item master, stock, history and POs a user sees
    // depend on which door they came through.
    heading: 'Inventory',
    items: [
      { to: '/inventory/boards', label: 'Boards', ready: true, perm: ['VIEW_INVENTORY', 'VIEW_BOARDS'] },
      { to: '/inventory/raw-material', label: 'Raw Material', ready: true, perm: 'VIEW_INVENTORY' },
      { to: '/inventory/spare-parts', label: 'Spare Parts', ready: true, perm: 'VIEW_INVENTORY' },
    ],
  },
  {
    heading: 'Admin',
    items: [
      { to: '/users', label: 'User Management', ready: true, perm: 'MANAGE_USERS' },
      { to: '/settings/sla', label: 'SLA Rules', ready: true, superAdminOnly: true },
    ],
  },
];

const SIDEBAR_KEY = 'kpjm.sidebarHidden';

export function Layout({ children }: { children: ReactNode }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  // Pending edit-request count: a small red badge appears on the Job Edit
  // Requests sidebar link whenever this GM / Super Admin has work waiting.
  // Polls every 60s so a request Manoj raises from another browser shows up
  // without a manual refresh.  Silent if the user does not hold the perm.
  const canApproveEdits = can(user, 'APPROVE_JOB_EDIT');
  const pendingEdits = useQuery({
    enabled: canApproveEdits,
    queryKey: ['pending-edit-requests'],
    queryFn: () => api<{ rows: { requestId: number }[] }>(`/api/job-orders/edit-requests?status=Pending`)
      .then((r) => r.rows.length),
    refetchInterval: 60_000,
    staleTime: 30_000,
  });
  const pendingCount = pendingEdits.data ?? 0;

  const [hidden, setHidden] = useState(() => {
    try { return localStorage.getItem(SIDEBAR_KEY) === '1'; } catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem(SIDEBAR_KEY, hidden ? '1' : '0'); } catch { /* private mode */ }
  }, [hidden]);

  // On mobile the sidebar is an overlay; close it on navigation.
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  return (
    <div className="min-h-screen flex flex-col layout-root">
      <header className="bg-brand text-white shadow print:hidden">
        <div className="flex h-14 items-center justify-between px-4">
          <div className="flex items-center gap-3 min-w-0">
            <button
              type="button"
              onClick={() => {
                const isMobile = window.innerWidth < 768;
                if (isMobile) setMobileOpen((o) => !o);
                else setHidden((h) => !h);
              }}
              title={hidden ? 'Show menu' : 'Hide menu for a wider view'}
              aria-label={hidden ? 'Show menu' : 'Hide menu'}
              aria-expanded={!hidden}
              className="rounded p-1.5 hover:bg-brand-700 shrink-0"
            >
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                {mobileOpen
                  ? <><path d="M5 5l10 10M15 5L5 15" /></>
                  : hidden
                    ? <><path d="M3 5h14M3 10h14M3 15h14" /></>
                    : <><path d="M5 5l10 10M15 5L5 15" /></>}
              </svg>
            </button>
            <span className="text-sm md:text-lg font-bold tracking-wide truncate">VASUDEV PRINT PACK PVT. LTD.</span>
          </div>
          <div className="flex items-center gap-3 text-sm">
            {(canApproveEdits || user?.rollId === ROLE.CRM || user?.rollId === ROLE.Designers) && <NotificationsToggle />}
            <span className="opacity-90 hidden sm:inline">{user?.name}</span>
            <button
              className="rounded bg-brand-700 px-3 py-1 text-xs hover:bg-brand-800"
              onClick={() => { logout(); navigate('/login'); }}
            >
              Logout
            </button>
          </div>
        </div>
      </header>

      <div className="flex flex-1 layout-container">
        {mobileOpen && (
          <div className="fixed inset-0 z-40 bg-black/40 md:hidden" onClick={() => setMobileOpen(false)} />
        )}
        <aside className={`
          ${mobileOpen ? 'fixed inset-y-0 left-0 z-50 w-64 shadow-xl' : 'hidden'}
          ${hidden ? '' : 'md:relative md:block md:w-60 md:shadow-none'}
          shrink-0 bg-slate-900 text-slate-200 print:hidden overflow-y-auto
        `}>
          <nav className="py-3 pt-16 md:pt-3">
            {SECTIONS.map((s) => ({
              ...s,
              items: s.items.filter((it) => {
                // A CRM works one screen and one queue, plus Job Orders to look
                // a job card up (view only — they hold no CREATE_JOB / EDIT_JOB).
                // This is a SCOPE rule, not a capability one — they can read
                // everything, we just do not want the whole master database in
                // their way — so it stays keyed on the role rather than on a
                // permission.
                if (user?.rollId === ROLE.CRM) {
                  return it.to === '/dashboard'
                      || it.to === '/queue/crm'
                      || it.to === '/job-orders'
                      || it.to === '/wip-report';
                }
                if (user?.rollId === ROLE.Planner) {
                  return it.to === '/dashboard'
                      || it.to === '/queue/cutting'
                      || it.to === '/planner'
                      || it.to === '/inventory/boards'
                      || it.to === '/wip-report';
                }
                // Accounts: the dashboard, the client and vendor masters, and
                // the inventories to look at (VIEW_INVENTORY — no In, no Issue).
                if (user?.rollId === ROLE.Accounts) {
                  return it.to === '/dashboard' || it.to === '/clients' || it.to === '/vendors'
                      || (it.to.startsWith('/inventory/') && can(user, 'VIEW_INVENTORY'));
                }
                // Quality approves and rejects production, and looks job cards
                // up on Job Orders (view only - they hold no CREATE_JOB / EDIT_JOB).
                if (user?.rollId === ROLE.Quality) {
                  return it.to === '/dashboard' || it.to === '/quality' || it.to === '/quality/master-files'
                      || it.to === '/job-orders';
                }
                if (user?.rollId === ROLE.Printing) {
                  return it.to === '/dashboard' || it.to === '/queue/printing';
                }
                // Store: the greeting dashboard and the Raw Material page (Issue).
                if (user?.rollId === ROLE.Store) {
                  return it.to === '/dashboard' || it.to === '/inventory/raw-material';
                }
                if (user?.rollId === ROLE.Cutting) {
                  return it.to === '/dashboard' || it.to === '/queue/cutting';
                }
                // Designers see Dashboard + Design Queue + (later,
                // once Session D lands) the performance / KPI reports.
                // Everything else — Masters, Transactions, Reports,
                // Admin — is somebody else's job and stays out of
                // their sidebar.
                if (user?.rollId === ROLE.Designers) {
                  return it.to === '/dashboard'
                      || it.to === '/queue/design'
                      || it.to === '/artworks'
                      || it.to === '/barcodes'
                      || it.to === '/job-orders'
                      || it.to === '/reports/performance';
                }
                // Roles that work the flow rather than maintain the catalogue.
                // Client/machine/sheet definitions are not their job, so the
                // Master Database block is dropped from their sidebar — all of
                // it, except Vendor Master for Purchase, who keep the vendors
                // they buy from.  Same reasoning as the CRM rule above: a scope
                // decision about what a person works on, not a statement about
                // what they may read — the routes still resolve if they
                // navigate directly.
                // Purchase keeps Vendor Master; the Process Coordinator keeps
                // Vendor Master and Client Master (fills in client details).
                if (s.heading === 'Master Database' && user?.rollId === ROLE.Purchase) {
                  return it.to === '/vendors';
                }
                if (s.heading === 'Master Database' && user?.rollId === ROLE.PC) {
                  return it.to === '/vendors' || it.to === '/clients';
                }
                if (s.heading === 'Master Database'
                    && (user?.rollId === ROLE.Admin || user?.rollId === ROLE.Dispatch)) {
                  return false;
                }
                if (user?.rollId === ROLE.Admin
                    && (it.to === '/purchase-orders' || it.to === '/production'
                        || it.to === '/dod-report' || it.to === '/job-orders' || it.to === '/reports')) {
                  return false;
                }
                // Purchase buys boards and stores: customer POs, packaging
                // slips and production entry are other desks' work (the
                // routes and the API keep them out too).
                if (user?.rollId === ROLE.Purchase
                    && (it.to === '/purchase-orders' || it.to === '/slips' || it.to === '/production')) {
                  return false;
                }
                if (it.superAdminOnly && user?.rollId !== ROLE.SuperAdmin) return false;
                return !it.perm || (Array.isArray(it.perm) ? it.perm.some((p) => can(user, p)) : can(user, it.perm));
              }),
            })).filter((s) => s.items.length > 0).flatMap((s) => {
              const sectionEl = (
                <div key={s.heading} className="px-3 pb-2">
                  <div className="px-2 pt-3 pb-1 text-[10px] font-semibold uppercase tracking-wider text-slate-400">
                    {s.heading}
                  </div>
                  <ul>
                    {s.items.map((it) =>
                      it.ready ? (
                         <li key={it.to}>
                          <NavLink
                            to={it.to}
                            // Exact match when another link sits under this one
                            // (Quality / Master File Orders), so only one lights up.
                            end={SECTIONS.some((s) => s.items.some((x) => x.to.startsWith(`${it.to}/`)))}
                            className={({ isActive }) =>
                              `flex items-center justify-between rounded px-3 py-1.5 text-sm ${
                                isActive ? 'bg-brand-600 text-white' : 'hover:bg-slate-800'
                              }`
                            }
                          >
                            <span>{it.label}</span>
                            {it.to === '/edit-requests' && pendingCount > 0 && (
                              <span
                                className="inline-flex items-center justify-center rounded-full bg-red-500 text-white text-[10px] font-bold min-w-[18px] h-[18px] px-1"
                                title={`${pendingCount} pending edit request${pendingCount === 1 ? '' : 's'}`}
                              >
                                {pendingCount > 99 ? '99+' : pendingCount}
                              </span>
                            )}
                          </NavLink>
                        </li>
                      ) : (
                        <li key={it.to}>
                          <div
                            className="flex items-center justify-between rounded px-3 py-1.5 text-sm text-slate-500 cursor-not-allowed"
                            title="Not built yet"
                          >
                            <span>{it.label}</span>
                            <span className="text-[9px] uppercase tracking-wider text-slate-600">soon</span>
                          </div>
                        </li>
                      ),
                    )}
                  </ul>
                </div>
              );
              return [sectionEl];
            })}
          </nav>
        </aside>

        <main className="flex-1 min-w-0 overflow-hidden bg-slate-50 layout-main">{children}</main>
      </div>
    </div>
  );
}

/**
 * Header bell that turns on push notifications: edit requests for approvers
 * (APPROVE_JOB_EDIT — GM + Super Admin), artwork sent for approval for CRMs,
 * and approval received for designers.  On click it asks for permission;
 * once granted the browser subscribes and hands the server a route to push
 * to.  Filled bell = subscribed, outline bell = permission not asked or was
 * denied.
 */
function NotificationsToggle() {
  const [status, setStatus] = useState<'unsupported' | 'default' | 'granted' | 'denied' | 'busy'>(
    'default',
  );
  useEffect(() => {
    if (!isPushSupported()) { setStatus('unsupported'); return; }
    void currentPushStatus().then((s) => setStatus(s));
  }, []);
  if (status === 'unsupported') return null;

  const enabled = status === 'granted';

  const click = async () => {
    if (status === 'busy') return;
    setStatus('busy');
    try {
      const r = await enablePushNotifications();
      setStatus(r === 'ok' ? 'granted' : 'denied');
    } catch (e) {
      alert((e as Error).message);
      setStatus('default');
    }
  };

  return (
    <button
      onClick={click}
      className={`relative rounded p-1 hover:bg-brand-700 ${enabled ? 'text-white' : 'text-white/70'}`}
      title={enabled ? 'Push notifications on' : status === 'denied'
        ? 'Blocked — enable notifications for this site in browser settings'
        : 'Turn on push notifications'}
      aria-label="Notifications"
    >
      <svg width="20" height="20" viewBox="0 0 24 24" fill={enabled ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
        <path d="M13.73 21a2 2 0 0 1-3.46 0" />
      </svg>
      {enabled && (
        <span className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-emerald-400 border-2 border-brand" />
      )}
    </button>
  );
}

